import { AgGridReact } from "ag-grid-react";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { getRowId } from "utils/grid-utils";
import { AppContext } from "context/AppProvider";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { resetGridFilters } from "entity-app/utils/utils";
import { GridApi } from "ag-grid-enterprise";
import { FilterItem } from "models/types";
import SubmittalListFilterComponent from "pages/submittals-list/filter-bar/submittal-list-filter-bar";
import ErrorBoundary from "components/error-boundary";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import { GridStatusMessage } from "components/widgets/grid-status-message";
import { useShowDesignAndSpec } from "hooks/common";
import { matchUserProjectRole } from "utils/utils";
import ImportACCSubmittalLogs from "components/import-acc-submittal-logs";
import ImportSubmittalLogs from "components/import-submittal-logs";
import { Button, message, Modal } from "antd";
import { FeatureTypes } from "entity-app/constants";
import FeatureBulkEditDurationsNewModel from "components/feature-bulk-edit-durations/feature-bulk-edit-durations-new-model";
import { OpenNotification } from "pages/submittals-log-page/components/open-notification";
import SubmittalLogCreateComponentNewModel from "pages/submittal-log-create/submittal-log-create-new-model";
import { InfoFeatureType } from "change-events/change-event-polling";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import {
  ESubmittalStatus,
  EUserTypes,
  IntegrationLinkTooltipMsg,
  ProjectPermissionEnum,
  SidepanelStyles,
  submittalLogFilterTitles,
  SubmittalStatusToStrMap,
  SuccessMessages,
  priorities
} from "constants/index";
import { getUser } from "services/auth";
import { applySubmittalLogFilters } from "utils/ag-grid-programmatic-filtering";

import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import CreateExtractMaterialFromSubmittal from "pages/material-extracted-log/components/create-material-from-submittal";
import BulkEditSubmittalDetailsNewModel from "./bulk-edit-submittals/bulk-edit-submittal-details-new-model";

import useFeatureSubmittalLogData from "./hooks/submittal-log-data";
import useSubmittalLogGridConfig from "./hooks/submittal-log-grid-config";
import useSubmittalIntegration from "./hooks/submittal-integration";

function SubmittalLog() {
  const { projectId } = useParams() as { projectId: string };
  const history = useHistory();

  const {
    tokenRetrievalState,
    isProcoreProject,
    projectDetails,
    projectIntegrationConfig,
    columnHeaders: { submittalHeaderMap },
    tokenContents,
    gqlClientForProject,
    loggedInUserProjectProfileResponse
  } = useContext(ProjectContext);

  const { submittalListFilter, setSubmittalListFilter } =
    useContext(AppContext);

  const isCurrentUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isInlineEditSubmittalPermissionGranted = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.InlineEditSubmittal,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const isViewLinkingPage = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.ViewLinkingPage,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const toolTipMsg =
    projectIntegrationConfig &&
    projectIntegrationConfig.length > 0 &&
    IntegrationLinkTooltipMsg[
      projectIntegrationConfig[0]?.integration
        ?.system as keyof typeof IntegrationLinkTooltipMsg
    ];

  const projectToken = tokenRetrievalState?.token;

  const gridRef = useRef<AgGridReact<any>>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const existingSubmittalsCountRef = useRef<any>(null);

  const {
    submittalList: submittalListFull,
    maxNumberOfMilestones,
    submittalTypesMap,
    statusesMap,
    WFProjectParticipants,
    isIntegrationConfigured,
    isIntegrationMode,
    submittalListLoading,
    submittalListLoadedOnce,
    fetchLogData,
    mutationDeleteSubmittal,
    setOnDataUpdateCallback
  } = useFeatureSubmittalLogData(projectId, projectToken);

  const { currentSourceType } = useSubmittalIntegration();

  const {
    gridDefaultColDef,
    gridColumnDefs,
    dateBlockColumnsDefs,
    saveColumnState,
    setColumnVisibility,
    applyColumnConfig,
    onEditCell
  } = useSubmittalLogGridConfig(projectId, gridRef, maxNumberOfMilestones);

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: fetchLogData,
    infoFeatureType: InfoFeatureType.submittal
  });

  const { isDesignTabEnabled } = useShowDesignAndSpec();

  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
  const [stats, setStats] = useState<any>("");
  const [displayRowCount, setDisplayRowCount] = useState(0);

  const [filters, setFilters] = useState<FilterItem[]>([]);

  const [isShowAll, setShowAll] = useState(true);

  const [showSubmittalBulkEditDetails, setShowSubmittalBulkEditDetails] =
    useState(false);
  const [showSubmittalBulkEditDurations, setShowSubmittalBulkEditDurations] =
    useState(false);
  const [
    showSubmittalBulkEditDeadlineDate,
    setShowSubmittalBulkEditDeadlineDate
  ] = useState(false);

  const [showExtractMaterial, setShowExtractMaterial] = useState(false);

  const [showImportLogDrawer, setshowImportLogDrawer] =
    useState<boolean>(false);

  const [showNewLogDrawer, setshowNewLogDrawer] = useState<boolean>(false);

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);

  useEffect(() => {
    if (
      submittalListFull &&
      submittalListFull.length > 0 &&
      setSelectedRows.length > 0
    ) {
      setSelectedRows(gridRef.current?.api?.getSelectedRows() || []);
    }
  }, [submittalListFull]);

  const submittalList = useMemo(() => {
    if (!isProcoreProject) {
      return submittalListFull;
    }

    return submittalListFull?.filter(
      (sub: any) => sub?.deleted_from_source !== isShowAll
    );
  }, [isProcoreProject, submittalListFull, isShowAll]);

  const submittalCount = useMemo(() => {
    const deletedSubCount: any = submittalListFull?.filter(
      (sub: any) => sub?.deleted_from_source
    );
    return {
      allSubCount: submittalListFull?.length || 0,
      deletedSubCount: deletedSubCount?.length || 0
    };
  }, [submittalListFull]);

  const onAllSubmittalsClick = useCallback(() => {
    resetGridFilters({
      setListFilter: setSubmittalListFilter,
      api: gridRef.current?.api
    });
  }, [setSubmittalListFilter, gridRef]);

  const updateStats = useCallback(() => {
    if (!gridRef.current?.api) return;
    const length = submittalList?.length || 0;
    const displayedRowCount = gridRef.current?.api?.getDisplayedRowCount();
    if (displayedRowCount === undefined) return;

    if (submittalListFilter && submittalListFilter.key) {
      const titleText = submittalLogFilterTitles[submittalListFilter.key];
      const statsContents = (
        <>
          <span>
            {displayedRowCount} of {length}
          </span>
          <span className="px-1">{titleText}</span>
        </>
      );
      setStats(statsContents);
      setDisplayRowCount(displayedRowCount || 0);
      return;
    }

    if (gridRef.current?.api?.isAnyFilterPresent()) {
      // Procore Project Init
      if (isProcoreProject) {
        const len = isShowAll
          ? submittalCount.allSubCount - submittalCount.deletedSubCount
          : submittalCount.deletedSubCount;

        setTimeout(() => {
          if (displayedRowCount === len) {
            setStats(displayedRowCount);
          } else {
            setStats(`${displayedRowCount} of ${len}`);
          }
          setDisplayRowCount(displayedRowCount || 0);
        }, 500);
        return;
      }
      //  Procore Project Init End

      setTimeout(() => {
        setStats(`${displayedRowCount} of ${length}`);
        setDisplayRowCount(displayedRowCount || 0);
      }, 500);
      return;
    }

    setStats(`${displayedRowCount}`);
    setDisplayRowCount(displayedRowCount);
  }, [
    submittalList?.length,
    submittalListFilter,
    isProcoreProject,
    submittalCount.allSubCount,
    submittalCount.deletedSubCount,
    isShowAll
  ]);

  useEffect(() => {
    setOnDataUpdateCallback(() => {
      updateStats();
    });
    return () => {
      setOnDataUpdateCallback(null);
    };
  }, [updateStats, setOnDataUpdateCallback]);

  const handleSaveColumnState = useCallback(() => {
    if (submittalListFilter && submittalListFilter.key) return;
    saveColumnState();
  }, [saveColumnState, submittalListFilter]);

  const onFirstDataRendered = () => {
    const loggedInUserCompany =
      loggedInUserProjectProfileResponse?.data?.subscription_users[0]?.user
        .project_users[0]?.subscription_vendor?.name;
    applyColumnConfig({ type: "default" });
    const loggedInUserInfo = getUser();
    if (submittalListFilter && submittalListFilter.key) {
      applySubmittalLogFilters({
        gridRef,
        loggedInUserInfo,
        submittalListFilter,
        SubmittalStatusToStrMap,
        additionalData: {
          loggedInUserCompany
        }
      });
    }
    updateStats();
    setTimeout(() => {
      setIsFirstDataRendered(true);
    }, 500);
  };

  const setFiltersForHeader = useCallback(() => {
    const filtersApplied = gridRef.current?.api?.getFilterModel();
    // get columnDefs from gridRef
    const columnDefs = gridRef.current?.api?.getColumnDefs();
    if (filtersApplied) {
      const items: FilterItem[] = new Array<FilterItem>();
      Object.keys(filtersApplied).forEach((key: any) => {
        const field = columnDefs!.filter((x: any) => x.colId === key)[0];
        const filter = filtersApplied[key];

        items.push({
          field: key,
          header: field ? field.headerName : key,
          filter
        });
      });

      setFilters(items);
    }
  }, []);

  const onSearch = useCallback((text: string) => {
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const getColumnStateFromGrid = () => {
    if (gridRef.current) {
      return UtilColumnConfig.getColumnStateFromGrid({
        gridRef: {
          api: gridRef.current!.api
        }
      });
    }
    return null;
  };

  const exportLogAsCSV = () => {
    if (gridRef.current && projectDetails)
      exportEntityLogAsCSV({
        entityName: "Submittal Log",
        projectDetails,
        gridRef: {
          api: gridRef.current!.api
        },
        includedColumns: ["description"],
        excludedColumns: ["_milestones", "_durations", "action"]
      });
  };

  const onSubmittalsEdit = (
    panel: "details" | "duration" | "deadline_date" | "extract_material"
  ) => {
    if (panel === "details") {
      setShowSubmittalBulkEditDetails(true);
    }
    if (panel === "duration") {
      setShowSubmittalBulkEditDurations(true);
    }
    if (panel === "deadline_date") {
      setShowSubmittalBulkEditDeadlineDate(true);
    }
    if (panel === "extract_material") {
      setShowExtractMaterial(true);
    }
  };

  const handleDeleteSubmittal = useCallback(
    async (submittalId: string[]) => {
      const deletedSubmittalsRes = await mutationDeleteSubmittal({
        variables: {
          feature_instance_ids: submittalId,
          feature_type_id: 1
        }
      });
      if (deletedSubmittalsRes.data) {
        message.success(SuccessMessages.deleteSuccess);
        fetchLogData();
      }
      if (deletedSubmittalsRes.errors) {
        console.error(deletedSubmittalsRes.errors);
        message.error("Failed to delete Submittals.");
      }
    },
    [mutationDeleteSubmittal, fetchLogData]
  );

  const headerComponentViewModel: any = {
    gridRef,
    items: filters,
    onCreateLogClick: () => {
      setshowNewLogDrawer(true);
    },
    onImportLogClick: () => {
      existingSubmittalsCountRef.current = submittalList;
      setshowImportLogDrawer(true);
    },
    onSearch,
    stats,
    showFiterChips: !submittalListFilter,
    onAllSubmittalsClick,
    selectedRow: selectedRows,
    onExportCSV: exportLogAsCSV,
    onSubmittalEditClick: onSubmittalsEdit,
    isIntegrationMode,
    displayRowCount,
    isIntegrationConfigured,
    resetFilters: () => {
      gridRef?.current?.api?.setFilterModel(null);
    },
    getColumnStateFromGrid,
    setFilterStateFromData: (columnState: any) => {
      applyColumnConfig({ type: "custom", columnConfig: columnState });
    },
    afterApplyFilterCallBack: () => {},
    disabled: false,
    isGridDataRendered: false,
    handleDeleteSubmittal,
    onSubmittalDeleted: () => {
      fetchLogData();
    },
    subCounts: submittalCount,
    segmentAction: (val: any) => {
      setShowAll(val === "true");
    },
    showDeletedTxt: !isShowAll
  };

  if (submittalListFilter && loggedInUserProjectProfileResponse?.loading) {
    return <div />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <CiqGridViewWrapper
        additionalGridComponent={
          <GridStatusMessage
            {...{
              isIntegrationMode,
              isIntegrationConfigured,
              submittalListLoading:
                submittalListLoading ||
                (submittalList?.length > 0 && !isFirstDataRendered),
              submittalListData: isFirstDataRendered ? submittalList : null,
              submittalListLoadedOnce,
              projectId
            }}
          />
        }
        headerComponent={
          <ErrorBoundary>
            <SubmittalListFilterComponent {...headerComponentViewModel} />
          </ErrorBoundary>
        }
        gridInfo={{
          gridRef,
          defaultColDef: gridDefaultColDef,
          columnDefs: gridColumnDefs as any,
          rowData: submittalList,
          rowHeight: 62,
          getRowId,
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest,
          onFirstDataRendered,
          onSortChanged: (e: any) => {
            if (!submittalListFilter && e.source === "uiColumnSorted")
              handleSaveColumnState();
            setFiltersForHeader();
          },
          onFilterChanged: () => {
            filterChipRef.current?.onFilterApplied();
            handleSaveColumnState();
            updateStats();
            setFiltersForHeader();
          },
          onColumnVisible: (e: any) => {
            if (e.source === "toolPanelUi") {
              const hasMilestoneCol = e?.columns.find(
                (column: any) => column.colId === "_milestones"
              );

              const hasDurationCol = e?.columns.find(
                (column: any) => column.colId === "_durations"
              );

              if (hasMilestoneCol) {
                setColumnVisibility(
                  dateBlockColumnsDefs.dateBlockColumnIds.milestones as any,
                  e.visible
                );
              }

              if (hasDurationCol) {
                setColumnVisibility(
                  dateBlockColumnsDefs.dateBlockColumnIds.durations as any,
                  e.visible
                );
              }
              handleSaveColumnState();
            }
          },
          onDragStopped: () => {
            if (!submittalListFilter) handleSaveColumnState();
          },
          context: {
            typesMap: submittalTypesMap,
            statusesMap,
            tokenContents: tokenRetrievalState?.tokenContents,
            projectDetails,
            projectParticipants: WFProjectParticipants,
            isIntegrationMode,
            onEditCell,
            onViewSpecSection: () => {},
            isCurrentUserGC,
            isDesignTabEnabled,
            isViewLinkingPage,
            featureHeaderMap: submittalHeaderMap,
            canInlineEditDBOffset: isInlineEditSubmittalPermissionGranted,
            priorities,
            projectId,
            history,
            toolTipMsg,
            isGridCellEditable: (params: any) => {
              return params?.colDef?.editable;
            }
          },
          onSelectionChanged: ({ api }: { api: GridApi }) => {
            setSelectedRows(api.getSelectedRows());
          },
          tooltipShowMode: "whenTruncated",
          overlayNoRowsTemplate: "",
          suppressNoRowsOverlay: true,
          suppressLoadingOverlay: true,
          rowSelection: {
            mode: "multiRow",
            selectAll: "filtered",
            isRowSelectable: (node) => {
              const isRowSelectable =
                node?.data?.workflow_status.id !== ESubmittalStatus.VOID &&
                !node?.data?.deleted_from_source;
              return isRowSelectable;
            }
          },
          selectionColumnDef: {
            sortable: true,
            resizable: false,
            width: 40,
            suppressHeaderMenuButton: true,
            pinned: "left",
            lockPinned: true
          },
          suppressAggFuncInHeader: true,
          suppressDragLeaveHidesColumns: true,
          maintainColumnOrder: true,
          debounceVerticalScrollbar: true,
          enableCellTextSelection: true,
          onColumnResized: (params: any) => {
            if (params.finished) {
              // User has finished resizing, do something like save column state
              handleSaveColumnState();
            }
          },
          disableResizingAutoWidth: true
        }}
      />

      {showSubmittalBulkEditDetails && (
        <BulkEditSubmittalDetailsNewModel
          onCancelClick={() => {
            setShowSubmittalBulkEditDetails(false);
          }}
          onUpdateDone={() => {
            setShowSubmittalBulkEditDetails(false);
            gridRef?.current?.api?.deselectAll();
            fetchLogData();
          }}
          selectedRows={selectedRows}
          gqlClient={gqlClientForProject}
          setShowSubmittalEdit={setShowSubmittalBulkEditDetails}
          showSubmittalEdit={showSubmittalBulkEditDetails}
          projectId={projectId}
        />
      )}

      {selectedRows && showSubmittalBulkEditDeadlineDate && (
        <BulkEditDeadlineDateModal
          onUpdateDone={() => {
            setShowSubmittalBulkEditDeadlineDate(false);
            gridRef?.current?.api?.deselectAll();
            fetchLogData();
          }}
          setShowEdit={setShowSubmittalBulkEditDeadlineDate}
          showEdit={showSubmittalBulkEditDeadlineDate}
          selectedRows={selectedRows}
          columnHeaderMap={submittalHeaderMap}
          featureTypeId={FeatureTypes.SUBMITTALS}
        />
      )}

      {selectedRows && showExtractMaterial && (
        <CreateExtractMaterialFromSubmittal
          selectedRows={selectedRows}
          onCancel={() => setShowExtractMaterial(false)}
          onComplete={() => {
            setShowExtractMaterial(false);
            gridRef?.current?.api?.deselectAll();
          }}
          openDrawer={showExtractMaterial}
        />
      )}

      {showImportLogDrawer &&
        (isIntegrationMode ? (
          <ImportACCSubmittalLogs
            tokenRetrievalState={tokenRetrievalState}
            gqlClientForProject={gqlClientForProject}
            showImportLogDrawer={showImportLogDrawer}
            setDrawerOpen={setshowImportLogDrawer}
            onCancel={() => {
              setshowImportLogDrawer(false);
            }}
            currentSourceType={currentSourceType}
          />
        ) : (
          <ImportSubmittalLogs
            submittalHeaderMap={submittalHeaderMap}
            tokenRetrievalState={tokenRetrievalState}
            gqlClientForProject={gqlClientForProject}
            existingSubmittalsCount={existingSubmittalsCountRef.current}
            showImportLogDrawer={showImportLogDrawer}
            setDrawerOpen={setshowImportLogDrawer}
            onCancel={() => {
              setshowImportLogDrawer(false);
            }}
          />
        ))}

      {showNewLogDrawer && (
        <SubmittalLogCreateComponentNewModel
          onCancelClick={() => {
            setshowNewLogDrawer(false);
          }}
          onSubmittalCreated={(id: string, title: string) => {
            setshowNewLogDrawer(false);
            OpenNotification({
              projectId,
              id,
              title,
              placement: "top"
            });
          }}
          gqlClient={gqlClientForProject}
          showCreateSubModel={showNewLogDrawer}
          setDrawerOpen={setshowNewLogDrawer}
        />
      )}

      <Modal
        title="Bulk Edit Durations"
        className="custom-drawer with-padding-fix"
        style={SidepanelStyles.style}
        width="calc(100vw - 200px)"
        footer={[
          <div className="flex w-full space-x-3 justify-end" key={100}>
            <Button
              onClick={() => {
                setShowSubmittalBulkEditDurations(false);
              }}
              key={101}
            >
              Close
            </Button>
          </div>
        ]}
        open={showSubmittalBulkEditDurations}
        onCancel={() => {
          setShowSubmittalBulkEditDurations(false);
        }}
        destroyOnClose
        keyboard={false}
      >
        <ErrorBoundary>
          <FeatureBulkEditDurationsNewModel
            selectedRows={selectedRows!}
            featureTypeId={FeatureTypes.SUBMITTALS}
            onUpdateDone={() => {
              fetchLogData();
            }}
          />
        </ErrorBoundary>
      </Modal>
    </div>
  );
}

export default SubmittalLog;
